/**
 * ImageAssetUploader — used on the dealer dashboard for image uploads
 * that get shown to buyers on their /my-matches page. Handles both the
 * business card (default) and the advisor headshot via the `variant` prop.
 *
 * Images are stored inline as data URLs on the dealer document (small
 * assets, no separate CDN needed).
 */

import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Trash2, IdCard, UserCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB

const VARIANTS = {
  business_card: {
    title: 'Your Business Card',
    subtitle: 'Shown on every customer match page',
    endpoint: '/dealer/business-card',
    urlField: 'business_card_url',
    Icon: IdCard,
    prompt: 'Upload an image of your business card (PNG or JPG, max 2MB)',
    testidPrefix: 'business-card',
  },
  advisor_photo: {
    title: 'Your Advisor Photo',
    subtitle: 'Shown in the "My Advisor" panel on every buyer\'s match page',
    endpoint: '/dealer/advisor-photo',
    urlField: 'advisor_photo_url',
    Icon: UserCircle2,
    prompt: 'Upload a friendly headshot so buyers see who they\'re working with (PNG or JPG, max 2MB)',
    testidPrefix: 'advisor-photo',
  },
};

const BusinessCardUploader = ({ dealer, onChange, isDark, variant = 'business_card' }) => {
  const cfg = VARIANTS[variant] || VARIANTS.business_card;
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const cardUrl = dealer?.[cfg.urlField];

  const getToken = () =>
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('token');

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file (PNG, JPG)');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Image too large (max 2MB)');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const token = getToken();
      const res = await axios.post(
        `${API}${cfg.endpoint}`,
        { image_data: dataUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${cfg.title.replace('Your ', '')} uploaded`);
      onChange?.(res.data[cfg.urlField]);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!cardUrl) return;
    if (!window.confirm(`Remove your ${cfg.title.replace('Your ', '').toLowerCase()} from customer match pages?`)) return;
    try {
      const token = getToken();
      await axios.delete(`${API}${cfg.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${cfg.title.replace('Your ', '')} removed`);
      onChange?.(null);
    } catch (err) {
      toast.error('Could not remove image');
    }
  };

  const Icon = cfg.Icon;

  return (
    <div
      className={`rounded-2xl ${isDark ? 'bg-gray-900/60 border border-gray-800' : 'bg-white border border-gray-200'} p-5 sm:p-6 shadow-sm`}
      data-testid={`${cfg.testidPrefix}-uploader`}
    >
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-[#F2A900]" />
        <h3 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {cfg.title}
        </h3>
        <span className={`ml-auto text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {cfg.subtitle}
        </span>
      </div>

      {cardUrl ? (
        <div className="space-y-3">
          <div className={`relative rounded-xl overflow-hidden ring-1 ${isDark ? 'ring-gray-800 bg-gray-800' : 'ring-gray-200 bg-gray-50'}`}>
            <img
              src={cardUrl}
              alt={`${cfg.title} preview`}
              className="w-full max-w-md mx-auto block"
              data-testid={`${cfg.testidPrefix}-preview`}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-[#004E86] hover:bg-[#003d6b] text-white font-semibold"
              data-testid={`${cfg.testidPrefix}-replace-btn`}
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Replace
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleDelete}
              className="font-semibold"
              data-testid={`${cfg.testidPrefix}-delete-btn`}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Remove
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={`rounded-xl border-2 border-dashed p-8 text-center ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-300 bg-gray-50'}`}
        >
          <Upload className={`w-8 h-8 mx-auto mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {cfg.prompt}
          </p>
          <Button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="bg-[#F2A900] hover:bg-[#E89400] text-black font-semibold"
            data-testid={`${cfg.testidPrefix}-upload-btn`}
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Choose image
          </Button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${cfg.testidPrefix}-file-input`}
      />
    </div>
  );
};

export default BusinessCardUploader;

